import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  CalendarDays, Plus, Pencil, Trash2, X, CheckCircle2, XCircle, RotateCcw,
  Send, Rocket, ChevronLeft, ChevronRight, TableProperties, LayoutGrid, Award,
  ExternalLink, Info, AlertTriangle, Link2, Settings2, Store,
} from "lucide-react";
import { api, fmtNum } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useDataQuery } from "@/hooks/useDataQuery";
import { useDebounce } from "@/hooks/useDebounce";
import LoadingState from "@/components/ui/loading-state";
import { DatePicker, MonthPicker } from "@/components/ui/date-picker";
import { formatTanggal, hariIniISO, bulanIniISO, dateDariIso, isoDariDate } from "@/lib/tanggal";
import { useCabangOptions } from "@/lib/cabang";
import {
  PageHeader, TidakPunyaAkses, SearchInput, StatusBadge, SortTh, useSortState, sortRows,
  usePaginasi, Paginasi, unduhCsv, TombolEksporCsv, Modal, Field, inputCls, selectCls,
  textareaCls, InputCabang, PLATFORM_LABEL, useCampaignMap, namaCampaign, usePinHapus,
  PinHapusDialog,
} from "./_shared";

// ============ Enum tampilan (mirror backend routers/marketing/content.py — hanya label) ============
const CONTENT_PLATFORMS = ["instagram", "tiktok", "youtube"];
const CONTENT_FORMATS = ["reels", "feed", "carousel", "story", "tiktok_video", "live"];
const CONTENT_FORMAT_LABEL = {
  reels: "Reels", feed: "Feed", carousel: "Carousel", story: "Story", tiktok_video: "Video TikTok", live: "Live",
};
const CONTENT_PILLARS = [
  "edukasi", "trust", "produk", "promo", "testimoni", "bts", "meme",
  "comparison", "problem_solution", "authority", "community", "sales",
];
const CONTENT_PILLAR_LABEL = {
  edukasi: "Edukasi", trust: "Trust", produk: "Produk", promo: "Promo", testimoni: "Testimoni",
  bts: "Behind The Scenes", meme: "Meme", comparison: "Perbandingan", problem_solution: "Problem-Solution",
  authority: "Otoritas", community: "Komunitas", sales: "Sales",
};
const CONTENT_STATUS_META = {
  idea: { label: "Ide", chip: "bg-slate-100 text-slate-600" },
  draft: { label: "Draft", chip: "bg-sky-100 text-sky-700" },
  review: { label: "Review", chip: "bg-amber-100 text-amber-800" },
  approved: { label: "Disetujui", chip: "bg-emerald-100 text-emerald-800" },
  scheduled: { label: "Terjadwal", chip: "bg-violet-100 text-violet-700" },
  posted: { label: "Tayang", chip: "bg-emerald-600 text-white" },
};

// Transisi manual (PATCH .../status, tanpa approval) — MIRROR PERSIS
// `CONTENT_MANUAL_TRANSITIONS` di backend, HANYA dipakai untuk menyembunyikan
// tombol yang pasti ditolak backend (aturan #3 CLAUDE.md); backend tetap
// menegakkan aturan ini sendiri, jadi kalau owner mengubahnya di sana, ini
// wajib diubah juga di sini.
const MANUAL_TRANSITIONS = {
  idea: ["draft"],
  draft: ["idea", "review"],
  review: ["draft"],
  approved: ["scheduled"],
  scheduled: ["approved"],
};
const TRANSITION_LABEL = {
  "idea->draft": "Mulai Draft",
  "draft->idea": "Kembalikan ke Ide",
  "draft->review": "Ajukan untuk Review",
  "review->draft": "Kembalikan ke Draft (Revisi Sendiri)",
  "approved->scheduled": "Jadwalkan Tayang",
  "scheduled->approved": "Batalkan Jadwal",
};

// ============================================================================
// Brand / Toko marketing + Referensi konten
// ============================================================================
// DAFTAR TOKO ADALAH DAFTAR TERPISAH KHUSUS MARKETING, BUKAN cabang sistem
// (keputusan owner 31 Agu 2026). Karena itu ia TIDAK dibaca dari
// `useCabangOptions()` seperti field "Brand" di atasnya, melainkan dari
// `GET /marketing/content/toko` yang isinya bisa diatur owner sendiri —
// mendaftarkannya sebagai cabang akan memunculkan kelima nama itu di SELURUH
// dropdown cabang (penjualan, gudang, akuisisi, absensi, penggajian).
//
// Satu konten boleh ditujukan ke LEBIH DARI SATU toko, jadi nilainya selalu
// LARIK. Larik kosong dikirim apa adanya (`[]`), TIDAK PERNAH diubah jadi
// `null` — backend membedakan keduanya: `[]` = kosongkan, tidak dikirim =
// biarkan. Ini persis jebakan yang membuat "kosongkan campaign" tidak pernah
// berfungsi di halaman ini (lihat catatan di `simpanEdit`).

/** Baris referensi kosong untuk repeater di form. */
function barisReferensiKosong() {
  return { judul: "", url: "" };
}

/** Cek murah sisi-klien SEBELUM kirim — backend tetap menegakkan aturan yang
 *  sama (`_validate_referensi`), ini hanya supaya user tidak perlu menunggu
 *  round-trip untuk kesalahan yang jelas. Mengembalikan pesan galat atau "". */
function periksaReferensi(rows) {
  for (const r of rows || []) {
    const url = (r.url || "").trim();
    const judul = (r.judul || "").trim();
    if (!url) {
      if (judul) return `Referensi "${judul}" belum punya URL — isi URL-nya atau hapus barisnya`;
      continue;
    }
    if (!/^https?:\/\//i.test(url)) {
      return `URL referensi "${url.slice(0, 40)}" harus diawali http:// atau https://`;
    }
  }
  return "";
}

/** Buang baris kosong sebelum dikirim — user sering menekan "+ Link" lalu
 *  batal mengisinya, dan itu bukan galat. */
function bersihkanReferensi(rows) {
  return (rows || [])
    .map((r) => ({ judul: (r.judul || "").trim(), url: (r.url || "").trim() }))
    .filter((r) => r.url);
}

/** Multi-pilih toko berupa deretan chip yang bisa ditekan (bukan
 *  `<select multiple>` — ctrl+klik untuk memilih beberapa tidak pernah
 *  ditemukan sendiri oleh staf toko, dan di layar sentuh tidak bisa sama
 *  sekali). */
function PilihToko({ value, onChange, opsi, testid }) {
  const dipilih = value || [];
  const toggle = (t) => {
    onChange(dipilih.includes(t) ? dipilih.filter((x) => x !== t) : [...dipilih, t]);
  };
  if (!opsi.length) {
    return <p className="text-xs text-slate-400" data-testid={`${testid}-kosong`}>Daftar toko belum diatur.</p>;
  }
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testid}>
      {opsi.map((t) => {
        const aktif = dipilih.includes(t);
        return (
          <button key={t} type="button" onClick={() => toggle(t)} data-testid={`${testid}-${t}`}
                  aria-pressed={aktif}
                  className={`h-8 px-3 rounded-full border text-xs font-medium transition ${
                    aktif ? "bg-emerald-600 border-emerald-600 text-white"
                          : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
            {t}
          </button>
        );
      })}
    </div>
  );
}

/** Repeater "Referensi Konten" — beberapa link sekaligus, masing-masing
 *  Judul + URL. */
function EditorReferensi({ value, onChange, testid }) {
  const rows = value || [];
  const ubah = (i, patch) => onChange(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const hapus = (i) => onChange(rows.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-2" data-testid={testid}>
      {rows.map((r, i) => (
        <div key={i} className="flex items-start gap-2">
          <input value={r.judul} onChange={(e) => ubah(i, { judul: e.target.value })}
                 placeholder="Nama/judul referensi" data-testid={`${testid}-judul-${i}`}
                 className={`${inputCls} h-9 flex-1`} />
          <input value={r.url} onChange={(e) => ubah(i, { url: e.target.value })}
                 placeholder="https://..." data-testid={`${testid}-url-${i}`}
                 className={`${inputCls} h-9 flex-[1.4]`} />
          <button type="button" onClick={() => hapus(i)} data-testid={`${testid}-hapus-${i}`}
                  className="h-9 w-9 shrink-0 rounded-lg border border-slate-200 grid place-items-center text-slate-400 hover:text-rose-600 hover:bg-rose-50">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      ))}
      <button type="button" onClick={() => onChange([...rows, barisReferensiKosong()])} data-testid={`${testid}-tambah`}
              className="h-8 px-3 rounded-lg border border-dashed border-slate-300 text-xs font-medium text-slate-600 hover:bg-slate-50 inline-flex items-center gap-1.5">
        <Plus className="w-3.5 h-3.5" /> Tambah Link Referensi
      </button>
      <p className="text-[10px] text-slate-400">URL wajib diawali http:// atau https:// supaya benar-benar bisa diklik.</p>
    </div>
  );
}

/** Daftar referensi mode baca — `<a>` sungguhan (target blank + rel noopener),
 *  bukan teks yang kelihatan seperti link. */
function DaftarReferensi({ items, testid }) {
  if (!items || !items.length) return null;
  return (
    <div data-testid={testid}>
      <p className="text-[11px] font-semibold text-slate-500 mb-1 inline-flex items-center gap-1.5">
        <Link2 className="w-3 h-3" /> Referensi Konten
      </p>
      <ul className="space-y-0.5">
        {items.map((r, i) => (
          <li key={`${r.url}-${i}`} className="text-xs">
            <a href={r.url} target="_blank" rel="noopener noreferrer" data-testid={`${testid}-link-${i}`}
               className="text-emerald-700 hover:underline inline-flex items-center gap-1 break-all">
              {r.judul || r.url} <ExternalLink className="w-3 h-3 shrink-0" />
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Pengaturan daftar toko (tambah/ubah/hapus) — satu PUT untuk seluruh
 *  daftar, sama seperti backend-nya. */
function KelolaTokoModal({ open, items, onClose, onSaved }) {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) setRows([...(items || [])]); }, [open, items]);

  const simpan = async (paksa = false) => {
    const bersih = rows.map((r) => (r || "").trim()).filter(Boolean);
    if (!bersih.length) { toast.error("Minimal satu toko harus ada"); return; }
    setBusy(true);
    try {
      const r = await api.put("/marketing/content/toko", { items: bersih }, paksa ? { params: { paksa: true } } : undefined);
      toast.success(r.data.message || "Daftar toko disimpan");
      onSaved?.(r.data.items);
      onClose();
    } catch (e) {
      // 409 = ada toko yang dihapus/diganti ejaannya padahal masih dipakai
      // konten. Tawarkan paksa, jangan hanya menampilkan galat lalu buntu.
      if (e?.response?.status === 409 && window.confirm(`${e.response.data.detail}\n\nLanjutkan tetap menyimpan?`)) {
        // DITUNGGU (bukan `return simpan(true)`) supaya `finally` di bawah
        // tidak melepas kunci `busy` selagi simpan paksa masih terbang —
        // celah itu cukup untuk klik kedua yang mengirim PUT dua kali.
        await simpan(true);
        return;
      }
      toast.error(e?.response?.data?.detail || "Gagal menyimpan daftar toko");
    } finally { setBusy(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title="Daftar Brand / Toko Marketing" maxWidth="max-w-lg" testid="mkt-content-toko-modal"
           footer={<>
             <button onClick={onClose} className="h-10 px-4 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50">Batal</button>
             <button onClick={() => simpan(false)} disabled={busy} data-testid="mkt-content-toko-simpan"
                     className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50">
               {busy ? "Menyimpan..." : "Simpan Daftar"}
             </button>
           </>}>
      <p className="text-xs text-slate-500">
        Daftar ini KHUSUS Marketing dan terpisah dari daftar cabang perusahaan — menambah nama di sini
        tidak akan memunculkannya di dropdown cabang penjualan/gudang/absensi.
      </p>
      <div className="space-y-2">
        {rows.map((t, i) => (
          <div key={i} className="flex items-center gap-2">
            <input value={t} onChange={(e) => setRows((s) => s.map((x, idx) => (idx === i ? e.target.value : x)))}
                   data-testid={`mkt-content-toko-f-${i}`} className={`${inputCls} h-9`} />
            <button type="button" onClick={() => setRows((s) => s.filter((_, idx) => idx !== i))}
                    data-testid={`mkt-content-toko-hapus-${i}`}
                    className="h-9 w-9 shrink-0 rounded-lg border border-slate-200 grid place-items-center text-slate-400 hover:text-rose-600 hover:bg-rose-50">
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </div>
      <button type="button" onClick={() => setRows((s) => [...s, ""])} data-testid="mkt-content-toko-tambah"
              className="h-8 px-3 rounded-lg border border-dashed border-slate-300 text-xs font-medium text-slate-600 hover:bg-slate-50 inline-flex items-center gap-1.5">
        <Plus className="w-3.5 h-3.5" /> Tambah Toko
      </button>
      <p className="text-[10px] text-slate-400">
        Toko yang dihapus/diganti ejaannya tapi masih dipakai konten akan ditolak — pindahkan dulu kontennya.
      </p>
    </Modal>
  );
}

function geserBulan(bulanISO, delta) {
  const [y, m] = (bulanISO || bulanIniISO()).split("-").map(Number);
  const d = new Date(y, m - 1 + delta, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function buildGrid(bulanISO) {
  const [y, m] = (bulanISO || bulanIniISO()).split("-").map(Number);
  const first = new Date(y, m - 1, 1);
  const startOffset = (first.getDay() + 6) % 7; // Senin = 0
  const gridStart = new Date(y, m - 1, 1 - startOffset);
  const cells = [];
  for (let i = 0; i < 42; i++) {
    const d = new Date(gridStart);
    d.setDate(gridStart.getDate() + i);
    cells.push({ iso: isoDariDate(d), inMonth: d.getMonth() === m - 1, dayNum: d.getDate() });
  }
  return cells;
}

const HARI_LABEL = ["Sen", "Sel", "Rab", "Kam", "Jum", "Sab", "Min"];

/**
 * Kalender Konten (Marketing Fase 2, brief butir 1-5).
 *
 * Kunci izin `marketing_content` untuk semua endpoint `/content*` (lihat
 * docstring `backend/routers/marketing/content.py`) — TIDAK ada kunci
 * terpisah untuk snapshot performa, itu bagian dari konten yang sama.
 *
 * PENOLAKAN KONTEN BUKAN TERMINAL (beda dari campaign!): backend mengirim
 * konten ditolak balik ke status "draft", bukan status mati. UI di sini
 * SENGAJA memakai warna amber (bukan rose seperti campaign ditolak) dan
 * kalimat "perlu direvisi" (bukan "ditolak permanen") untuk banner itu, dan
 * banner hanya tampil selagi status MASIH "draft" — begitu diajukan ulang
 * dan berpindah status, banner otomatis hilang (lihat `RejectionBanner`).
 */
export default function ContentCalendar() {
  const { can } = useAuth();
  const bolehLihat = can("marketing_content", "view");
  const bolehTambah = can("marketing_content", "create");
  const bolehEdit = can("marketing_content", "update");
  const bolehHapus = can("marketing_content", "delete");
  const bolehCampaign = can("marketing_campaign", "view");
  const { campaignMap, campaignList } = useCampaignMap(bolehCampaign);
  const brandOpsi = useCabangOptions({ denganKosong: false });

  const [brand, setBrand] = useState("");
  useEffect(() => { if (!brand && brandOpsi.length) setBrand(brandOpsi[0]); }, [brand, brandOpsi]);

  // Daftar toko marketing — SUMBER TERPISAH dari daftar cabang (`brandOpsi`
  // di atas), lihat catatan besar di kepala berkas ini. `useMemo` bukan gaya:
  // `tokoQ.data?.items || []` melahirkan array BARU tiap render, dan array
  // seperti itu pernah menggagalkan build begitu dipakai sebagai dependensi
  // (AdsImport.jsx, lihat CLAUDE.md).
  const tokoQ = useDataQuery(
    ["marketing", "content-toko"],
    () => api.get("/marketing/content/toko").then((r) => r.data),
    { enabled: bolehLihat },
  );
  const tokoOpsi = useMemo(() => tokoQ.data?.items || [], [tokoQ.data]);
  const [tokoFilter, setTokoFilter] = useState("");
  const [kelolaToko, setKelolaToko] = useState(false);

  // Daftar PIC (id+nama) untuk dropdown create/edit — lihat docstring
  // `GET /users/untuk-penugasan` (routers/users.py): endpoint ringan tanpa
  // email/role, aman dipanggil siapa pun yang login. Hanya di-fetch kalau
  // user bisa membuat/mengubah konten — pengguna view-only tidak butuh ini.
  const picQ = useDataQuery(
    ["marketing", "users-untuk-penugasan"],
    () => api.get("/users/untuk-penugasan").then((r) => r.data),
    { enabled: bolehTambah || bolehEdit },
  );
  const picOptions = picQ.data || [];

  const [bulan, setBulan] = useState(bulanIniISO());
  const [tab, setTab] = useState("kalender"); // kalender | daftar | scorecard
  const [selectedId, setSelectedId] = useState(null);
  const [createPreset, setCreatePreset] = useState(null); // {date_plan} | null

  // Filter toko ikut ke SERVER (bukan disaring di layar): hasil kalender/daftar
  // dipotong di server, jadi menyaring potongannya di klien akan melaporkan
  // "tidak ada" untuk konten yang sebenarnya ada — jebakan yang sama dengan
  // daftar piutang/utang di CLAUDE.md. Nilainya juga masuk queryKey supaya
  // cache tidak menyodorkan hasil filter lama.
  const kalenderQ = useDataQuery(
    ["marketing", "content-kalender", brand, bulan, tokoFilter],
    () => api.get("/marketing/content/kalender", {
      params: { brand, bulan, ...(tokoFilter ? { toko: tokoFilter } : {}) },
    }).then((r) => r.data),
    { enabled: bolehLihat && !!brand },
  );

  // ---- Tab Daftar ----
  const [q, setQ] = useState("");
  const qDebounced = useDebounce(q, 350);
  const [statusFilter, setStatusFilter] = useState("");
  const [platformFilter, setPlatformFilter] = useState("");
  const [pillarFilter, setPillarFilter] = useState("");
  const { sortKey, sortDir, toggle } = useSortState("date_plan", "desc");
  const daftarQ = useDataQuery(
    ["marketing", "content-list", brand, statusFilter, platformFilter, pillarFilter, tokoFilter],
    () => api.get("/marketing/content", {
      params: {
        brand,
        ...(statusFilter ? { status: statusFilter } : {}),
        ...(platformFilter ? { platform: platformFilter } : {}),
        ...(pillarFilter ? { pillar: pillarFilter } : {}),
        ...(tokoFilter ? { toko: tokoFilter } : {}),
      },
    }).then((r) => r.data),
    { enabled: bolehLihat && !!brand && tab === "daftar" },
  );
  const daftarFiltered = useMemo(() => {
    const rows = daftarQ.data || [];
    const t = qDebounced.trim().toLowerCase();
    if (!t) return rows;
    return rows.filter((c) => c.title?.toLowerCase().includes(t) || c.code?.toLowerCase().includes(t));
  }, [daftarQ.data, qDebounced]);
  const daftarSorted = sortRows(daftarFiltered, sortKey, sortDir);
  const { page, setPage, totalPages, awal, akhir } = usePaginasi(daftarSorted.length, 15);
  const daftarHalaman = daftarSorted.slice(awal, akhir);

  // ---- Tab Scorecard ----
  const [metric, setMetric] = useState("views");
  const scorecardQ = useDataQuery(
    ["marketing", "content-scorecard", brand, bulan, metric, tokoFilter],
    () => api.get("/marketing/content/scorecard", {
      params: { brand, bulan, metric, ...(tokoFilter ? { toko: tokoFilter } : {}) },
    }).then((r) => r.data),
    { enabled: bolehLihat && !!brand && tab === "scorecard" },
  );

  const refetchSemua = () => {
    kalenderQ.refetch();
    daftarQ.refetch();
    scorecardQ.refetch();
  };

  const eksporDaftarCsv = () => {
    unduhCsv(`konten-${brand}-${hariIniISO()}.csv`, [
      { label: "Kode", value: (r) => r.code },
      { label: "Judul", value: (r) => r.title },
      // Larik digabung dengan "; " — koma sudah dipakai pemisah kolom CSV.
      { label: "Brand / Toko", value: (r) => (r.toko || []).join("; ") },
      { label: "Referensi", value: (r) => (r.referensi || []).map((x) => `${x.judul}: ${x.url}`).join("; ") },
      { label: "Platform", value: (r) => PLATFORM_LABEL[r.platform] || r.platform },
      { label: "Format", value: (r) => CONTENT_FORMAT_LABEL[r.format] || r.format },
      { label: "Pillar", value: (r) => CONTENT_PILLAR_LABEL[r.pillar] || r.pillar },
      { label: "Tanggal Rencana", value: (r) => r.date_plan },
      { label: "Deadline Posting", value: (r) => r.deadline_post || "" },
      { label: "Deadline Terlewat", value: (r) => (r.deadline_terlewat ? "Ya" : "Tidak") },
      { label: "Status", value: (r) => CONTENT_STATUS_META[r.status]?.label || r.status },
      { label: "PIC", value: (r) => r.pic_nama || "" },
      { label: "Campaign", value: (r) => namaCampaign(campaignMap, r.campaign_id) },
    ], daftarSorted);
  };

  const pindahTanggalCepat = async (cid, dateIso) => {
    try {
      const r = await api.patch(`/marketing/content/${cid}/tanggal`, { date_plan: dateIso });
      // Backend TIDAK PERNAH menolak pergeseran tanggal (itu keputusan manusia
      // yang sah), tapi mengembalikan peringatan lewat `message` kalau tanggal
      // baru melewati deadline_post — wajib ditampilkan, bukan ditelan diam-diam.
      if (r.data.deadline_terlewat) toast.warning(r.data.message);
      else toast.success(r.data.message || "Tanggal konten dipindah");
      refetchSemua();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal memindah tanggal konten"); }
  };

  const grid = useMemo(() => buildGrid(bulan), [bulan]);
  const byTanggal = kalenderQ.data?.by_tanggal || {};

  if (!bolehLihat) {
    return (
      <div className="p-4 md:p-8 space-y-5">
        <PageHeader icon={CalendarDays} title="Kalender Konten" />
        <TidakPunyaAkses label="Kalender Konten" testid="mkt-content-tanpa-akses" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 space-y-5" data-testid="mkt-content-calendar">
      <PageHeader
        icon={CalendarDays} title="Kalender Konten"
        desc="Rencana konten idea → draft → review → approved → scheduled → posted, per brand per bulan."
        actions={
          bolehTambah && (
            <button onClick={() => setCreatePreset({ date_plan: hariIniISO() })} data-testid="mkt-content-tambah"
                    className="h-9 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium inline-flex items-center gap-2">
              <Plus className="w-4 h-4" /> Tambah Konten
            </button>
          )
        }
      />

      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <select value={brand} onChange={(e) => setBrand(e.target.value)} data-testid="mkt-content-filter-brand"
                  className={`${selectCls} h-9 w-auto`}>
            {brandOpsi.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
          {/* Filter Brand/Toko — daftar KHUSUS marketing, bukan cabang.
              Berlaku untuk ketiga tab sekaligus. */}
          <div className="inline-flex items-center gap-1">
            <Store className="w-3.5 h-3.5 text-slate-400" />
            <select value={tokoFilter} onChange={(e) => setTokoFilter(e.target.value)} data-testid="mkt-content-filter-toko"
                    className={`${selectCls} h-9 w-auto`}>
              <option value="">Semua Toko</option>
              {tokoOpsi.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          {/* Kunci izin SAMA dengan `require_permission("marketing_content",
              "update")` di PUT /content/toko — tombol ini tidak boleh tampil
              untuk orang yang ujungnya kena 403. */}
          {bolehEdit && (
            <button onClick={() => setKelolaToko(true)} data-testid="mkt-content-kelola-toko"
                    title="Atur daftar Brand / Toko marketing"
                    className="h-9 px-2.5 rounded-lg border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 inline-flex items-center gap-1.5 text-xs font-medium">
              <Settings2 className="w-3.5 h-3.5" /> Atur Toko
            </button>
          )}
          {(tab === "kalender" || tab === "scorecard") && (
            <MonthPicker value={bulan} onChange={setBulan} data-testid="mkt-content-filter-bulan" className="w-32" />
          )}
        </div>
        <div className="inline-flex rounded-lg border border-slate-200 bg-white p-1 gap-1">
          <button onClick={() => setTab("kalender")} data-testid="mkt-content-tab-kalender"
                  className={`px-3 h-8 rounded-md text-xs font-medium inline-flex items-center gap-1.5 ${tab === "kalender" ? "bg-emerald-600 text-white" : "text-slate-600 hover:bg-slate-50"}`}>
            <LayoutGrid className="w-3.5 h-3.5" /> Kalender
          </button>
          <button onClick={() => setTab("daftar")} data-testid="mkt-content-tab-daftar"
                  className={`px-3 h-8 rounded-md text-xs font-medium inline-flex items-center gap-1.5 ${tab === "daftar" ? "bg-emerald-600 text-white" : "text-slate-600 hover:bg-slate-50"}`}>
            <TableProperties className="w-3.5 h-3.5" /> Daftar
          </button>
          <button onClick={() => setTab("scorecard")} data-testid="mkt-content-tab-scorecard"
                  className={`px-3 h-8 rounded-md text-xs font-medium inline-flex items-center gap-1.5 ${tab === "scorecard" ? "bg-emerald-600 text-white" : "text-slate-600 hover:bg-slate-50"}`}>
            <Award className="w-3.5 h-3.5" /> Scorecard
          </button>
        </div>
      </div>

      {tab === "kalender" && (
        !brand ? (
          <p className="text-sm text-slate-400 text-center py-10">Pilih brand dulu untuk melihat kalender.</p>
        ) : (
          <LoadingState loading={kalenderQ.loading} error={kalenderQ.error} onRetry={kalenderQ.refetch} varian="kartu" jumlahKartu={1} data-testid="mkt-content-kalender-loading">
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden" data-testid="mkt-content-kalender">
              <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
                <button onClick={() => setBulan((b) => geserBulan(b, -1))} data-testid="mkt-content-bulan-prev"
                        className="w-8 h-8 rounded-lg border border-slate-200 grid place-items-center hover:bg-slate-50">
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <p className="text-sm font-semibold text-slate-900">{formatBulanLabel(bulan)} · {kalenderQ.data?.total ?? 0} konten</p>
                <button onClick={() => setBulan((b) => geserBulan(b, 1))} data-testid="mkt-content-bulan-next"
                        className="w-8 h-8 rounded-lg border border-slate-200 grid place-items-center hover:bg-slate-50">
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
              <div className="grid grid-cols-7 text-[11px] font-semibold text-slate-500 bg-slate-50 border-b border-slate-100">
                {HARI_LABEL.map((h) => <div key={h} className="px-2 py-1.5 text-center">{h}</div>)}
              </div>
              <div className="grid grid-cols-7">
                {grid.map((cell) => (
                  <div key={cell.iso}
                       onDragOver={(e) => { if (bolehEdit) { e.preventDefault(); e.dataTransfer.dropEffect = "move"; } }}
                       onDrop={(e) => {
                         if (!bolehEdit) return;
                         e.preventDefault();
                         const cid = e.dataTransfer.getData("text/plain");
                         if (cid) pindahTanggalCepat(cid, cell.iso);
                       }}
                       data-testid={`mkt-content-cell-${cell.iso}`}
                       className={`group min-h-[92px] border-b border-r border-slate-100 p-1.5 align-top ${cell.inMonth ? "bg-white" : "bg-slate-50/60"}`}>
                    <div className="flex items-center justify-between">
                      <span className={`text-[11px] font-mono ${cell.inMonth ? "text-slate-500" : "text-slate-300"}`}>{cell.dayNum}</span>
                      {bolehTambah && cell.inMonth && (
                        <button onClick={() => setCreatePreset({ date_plan: cell.iso })} data-testid={`mkt-content-cell-tambah-${cell.iso}`}
                                className="w-5 h-5 rounded hover:bg-emerald-50 text-emerald-600 grid place-items-center opacity-0 hover:opacity-100 group-hover:opacity-100">
                          <Plus className="w-3 h-3" />
                        </button>
                      )}
                    </div>
                    <div className="space-y-1 mt-1">
                      {(byTanggal[cell.iso] || []).map((item) => (
                        <div key={item.id}
                             draggable={bolehEdit && item.status !== "posted"}
                             onDragStart={(e) => e.dataTransfer.setData("text/plain", item.id)}
                             onClick={() => setSelectedId(item.id)}
                             title={item.status !== "posted" ? "Geser ke tanggal lain untuk memindah rencana tayang" : "Konten sudah tayang — tidak bisa dipindah"}
                             data-testid={`mkt-content-card-${item.id}`}
                             className={`text-[10px] px-1.5 py-1 rounded border cursor-pointer hover:shadow-sm ${CONTENT_STATUS_META[item.status]?.chip || "bg-slate-100 text-slate-600"} ${item.deadline_terlewat ? "border-rose-500 ring-1 ring-rose-300" : "border-transparent"}`}>
                          <p className="font-medium truncate inline-flex items-center gap-1">
                            {item.deadline_terlewat && (
                              <AlertTriangle className="w-2.5 h-2.5 text-rose-600 shrink-0" data-testid={`mkt-content-card-deadline-${item.id}`} />
                            )}
                            <span className="truncate">{item.title}</span>
                          </p>
                          <p className="opacity-75 truncate">{PLATFORM_LABEL[item.platform] || item.platform}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-3 flex-wrap px-4 py-2.5 border-t border-slate-100">
                {Object.entries(CONTENT_STATUS_META).map(([k, v]) => (
                  <span key={k} className="inline-flex items-center gap-1 text-[10px] text-slate-500">
                    <span className={`w-2.5 h-2.5 rounded-full ${v.chip.split(" ")[0]}`} /> {v.label}
                  </span>
                ))}
              </div>
            </div>
          </LoadingState>
        )
      )}

      {tab === "daftar" && (
        <>
          <div className="flex items-center gap-2 flex-wrap justify-between">
            <div className="flex items-center gap-2 flex-wrap">
              <SearchInput value={q} onChange={setQ} placeholder="Cari judul/kode..." testid="mkt-content-cari" className="w-56" />
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="mkt-content-filter-status" className={`${selectCls} h-9 w-auto`}>
                <option value="">Semua Status</option>
                {Object.entries(CONTENT_STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
              <select value={platformFilter} onChange={(e) => setPlatformFilter(e.target.value)} data-testid="mkt-content-filter-platform" className={`${selectCls} h-9 w-auto`}>
                <option value="">Semua Platform</option>
                {CONTENT_PLATFORMS.map((p) => <option key={p} value={p}>{PLATFORM_LABEL[p]}</option>)}
              </select>
              <select value={pillarFilter} onChange={(e) => setPillarFilter(e.target.value)} data-testid="mkt-content-filter-pillar" className={`${selectCls} h-9 w-auto`}>
                <option value="">Semua Pillar</option>
                {CONTENT_PILLARS.map((p) => <option key={p} value={p}>{CONTENT_PILLAR_LABEL[p]}</option>)}
              </select>
            </div>
            <TombolEksporCsv onClick={eksporDaftarCsv} testid="mkt-content-export" />
          </div>

          <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600">
                  <tr>
                    <SortTh label="Judul" sortKeyName="title" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="mkt-content-sort-title" />
                    <th className="px-3 py-2.5 font-semibold">Platform / Format / Pillar</th>
                    <SortTh label="Tanggal Rencana" sortKeyName="date_plan" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="mkt-content-sort-tanggal" />
                    <SortTh label="Status" sortKeyName="status" sortKey={sortKey} sortDir={sortDir} onSort={toggle} testid="mkt-content-sort-status" />
                    <th className="px-3 py-2.5 font-semibold text-right">Aksi</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  <LoadingState loading={daftarQ.loading} error={daftarQ.error} onRetry={daftarQ.refetch}
                                kosong={!daftarQ.loading && !daftarQ.error && daftarHalaman.length === 0}
                                varian="tabel" kolom={5} pesanKosong="Belum ada konten yang cocok." data-testid="mkt-content-daftar-state">
                    {daftarHalaman.map((c) => (
                      <tr key={c.id} className="hover:bg-slate-50/60 cursor-pointer" onClick={() => setSelectedId(c.id)} data-testid={`mkt-content-row-${c.id}`}>
                        <td className="px-3 py-2.5">
                          <p className="font-mono text-[11px] text-slate-400">{c.code}</p>
                          <p className="font-medium text-slate-900">{c.title}</p>
                          {(c.toko || []).length > 0 && (
                            <p className="text-[10px] text-slate-500 mt-0.5" data-testid={`mkt-content-row-toko-${c.id}`}>
                              {(c.toko || []).join(" · ")}
                            </p>
                          )}
                          {(c.referensi || []).length > 0 && (
                            <p className="text-[10px] text-slate-400 mt-0.5 inline-flex items-center gap-1" data-testid={`mkt-content-row-referensi-${c.id}`}>
                              <Link2 className="w-2.5 h-2.5" /> {(c.referensi || []).length} referensi
                            </p>
                          )}
                        </td>
                        <td className="px-3 py-2.5 text-slate-600 text-xs">
                          {PLATFORM_LABEL[c.platform] || c.platform} · {CONTENT_FORMAT_LABEL[c.format] || c.format} · {CONTENT_PILLAR_LABEL[c.pillar] || c.pillar}
                        </td>
                        <td className="px-3 py-2.5 text-slate-600">
                          {formatTanggal(c.date_plan)}
                          {c.deadline_post && (
                            <p className={`text-[10px] mt-0.5 inline-flex items-center gap-1 ${c.deadline_terlewat ? "text-rose-600 font-medium" : "text-slate-400"}`}
                               data-testid={`mkt-content-row-deadline-${c.id}`}>
                              {c.deadline_terlewat && <AlertTriangle className="w-2.5 h-2.5" />}
                              Deadline {formatTanggal(c.deadline_post)}
                            </p>
                          )}
                        </td>
                        <td className="px-3 py-2.5"><StatusBadge meta={CONTENT_STATUS_META} value={c.status} /></td>
                        <td className="px-3 py-2.5 text-right">
                          <button onClick={(e) => { e.stopPropagation(); setSelectedId(c.id); }} data-testid={`mkt-content-detail-${c.id}`}
                                  className="inline-flex items-center gap-1 text-emerald-700 hover:underline text-xs font-medium">
                            Detail <ExternalLink className="w-3 h-3" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </LoadingState>
                </tbody>
              </table>
            </div>
            <Paginasi page={page} setPage={setPage} totalPages={totalPages} testid="mkt-content-paginasi" />
          </div>
        </>
      )}

      {tab === "scorecard" && (
        !brand ? (
          <p className="text-sm text-slate-400 text-center py-10">Pilih brand dulu untuk melihat scorecard.</p>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500">Urutkan berdasarkan</span>
              <select value={metric} onChange={(e) => setMetric(e.target.value)} data-testid="mkt-content-scorecard-metric" className={`${selectCls} h-9 w-auto`}>
                <option value="views">Views</option>
                <option value="profile_visits">Profile Visits</option>
              </select>
            </div>
            <p className="text-xs text-slate-500">
              Hanya konten berstatus "Tayang" yang ikut peringkat, dari snapshot performa TERBARU tiap konten (biasanya H+7).
              {scorecardQ.data && <> {scorecardQ.data.total_dengan_data} dari {scorecardQ.data.total_posted} konten tayang sudah punya data performa.</>}
            </p>
            <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-slate-600">
                    <tr className="text-left">
                      <th className="px-3 py-2.5 font-semibold w-12">Rank</th>
                      <th className="px-3 py-2.5 font-semibold">Konten</th>
                      <th className="px-3 py-2.5 font-semibold text-right">Views</th>
                      <th className="px-3 py-2.5 font-semibold text-right">Engagement</th>
                      <th className="px-3 py-2.5 font-semibold text-right">Profile Visits</th>
                      <th className="px-3 py-2.5 font-semibold">Snapshot</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    <LoadingState loading={scorecardQ.loading} error={scorecardQ.error} onRetry={scorecardQ.refetch}
                                  kosong={!scorecardQ.loading && !scorecardQ.error && (scorecardQ.data?.items || []).length === 0}
                                  varian="tabel" kolom={6} pesanKosong="Belum ada konten tayang dengan data performa bulan ini." data-testid="mkt-content-scorecard-state">
                      {(scorecardQ.data?.items || []).map((it) => (
                        <tr key={it.content_id} className="hover:bg-slate-50/60 cursor-pointer" onClick={() => setSelectedId(it.content_id)} data-testid={`mkt-content-scorecard-row-${it.content_id}`}>
                          <td className="px-3 py-2.5 font-mono text-slate-400">{it.rank}</td>
                          <td className="px-3 py-2.5">
                            <p className="font-mono text-[11px] text-slate-400">{it.code}</p>
                            <p className="font-medium text-slate-900">{it.title}</p>
                            <p className="text-[11px] text-slate-500">{PLATFORM_LABEL[it.platform] || it.platform} · {CONTENT_FORMAT_LABEL[it.format] || it.format}</p>
                          </td>
                          <td className={`px-3 py-2.5 text-right font-mono ${metric === "views" ? "font-bold text-slate-900" : ""}`}>{fmtNum(it.views)}</td>
                          <td className="px-3 py-2.5 text-right font-mono text-slate-500">{fmtNum((it.likes || 0) + (it.comments || 0) + (it.shares || 0) + (it.saves || 0))}</td>
                          <td className={`px-3 py-2.5 text-right font-mono ${metric === "profile_visits" ? "font-bold text-slate-900" : ""}`}>{fmtNum(it.profile_visits)}</td>
                          <td className="px-3 py-2.5 text-slate-500 text-xs">{formatTanggal(it.snapshot_date)}{it.h_plus != null ? ` (H+${it.h_plus})` : ""}</td>
                        </tr>
                      ))}
                    </LoadingState>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )
      )}

      {/* Form tambah konten cepat */}
      <ContentCreateModal open={!!createPreset} preset={createPreset} brand={brand} brandOpsi={brandOpsi}
                          tokoOpsi={tokoOpsi} tokoPreset={tokoFilter}
                          bolehCampaign={bolehCampaign} campaignList={campaignList} picOptions={picOptions}
                          onClose={() => setCreatePreset(null)}
                          onCreated={(item) => { setCreatePreset(null); refetchSemua(); setSelectedId(item.id); }} />

      {/* Detail + aksi status + performa */}
      <ContentDetailModal id={selectedId} onClose={() => setSelectedId(null)} onChanged={refetchSemua}
                          bolehEdit={bolehEdit} bolehHapus={bolehHapus} bolehTambah={bolehTambah}
                          bolehCampaign={bolehCampaign} campaignList={campaignList} campaignMap={campaignMap}
                          brandOpsi={brandOpsi} tokoOpsi={tokoOpsi} picOptions={picOptions} />

      {/* Pengaturan daftar toko marketing */}
      <KelolaTokoModal open={kelolaToko} items={tokoOpsi} onClose={() => setKelolaToko(false)}
                       onSaved={(items) => {
                         tokoQ.refetch();
                         // Filter yang menunjuk toko yang baru saja dihapus akan
                         // mengembalikan nol baris selamanya tanpa penjelasan —
                         // kembalikan ke "Semua Toko" begitu namanya lenyap.
                         if (tokoFilter && !items.includes(tokoFilter)) setTokoFilter("");
                       }} />
    </div>
  );
}

function formatBulanLabel(bulanISO) {
  const [y, m] = (bulanISO || bulanIniISO()).split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString("id-ID", { month: "long", year: "numeric" });
}

// ============================================================================
// Modal Tambah Konten
// ============================================================================
function ContentCreateModal({ open, preset, brand, brandOpsi, tokoOpsi, tokoPreset, bolehCampaign, campaignList, picOptions, onClose, onCreated }) {
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setForm({
        brand: brand || brandOpsi[0] || "", campaign_id: "", date_plan: preset?.date_plan || hariIniISO(),
        platform: CONTENT_PLATFORMS[0], format: CONTENT_FORMATS[0], pillar: CONTENT_PILLARS[0],
        title: "", hook: "", caption: "", cta: "", asset_url: "", catatan: "",
        pic_user_id: "", deadline_post: "",
        // Toko yang sedang difilter dipakai sebagai isian awal — konten yang
        // dibuat dari tampilan satu toko hampir selalu untuk toko itu.
        toko: tokoPreset ? [tokoPreset] : [], referensi: [],
      });
    }
  }, [open, preset, brand, brandOpsi, tokoPreset]);

  const simpan = async () => {
    if (!form.brand.trim()) { toast.error("Brand wajib diisi"); return; }
    if (!form.title.trim()) { toast.error("Judul wajib diisi"); return; }
    if (!form.date_plan) { toast.error("Tanggal rencana tayang wajib diisi"); return; }
    const galatRef = periksaReferensi(form.referensi);
    if (galatRef) { toast.error(galatRef); return; }
    setBusy(true);
    try {
      const r = await api.post("/marketing/content", {
        ...form, campaign_id: form.campaign_id || null,
        pic_user_id: form.pic_user_id || null, deadline_post: form.deadline_post || null,
        // Larik dikirim APA ADANYA — `form.toko || null` akan membuat backend
        // memperlakukannya sebagai "tidak dikirim", bukan "kosong".
        toko: form.toko, referensi: bersihkanReferensi(form.referensi),
      });
      toast.success("Konten ditambahkan");
      onCreated?.(r.data.item);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menambah konten"); }
    finally { setBusy(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title="Tambah Konten" maxWidth="max-w-2xl" testid="mkt-content-create-modal"
           footer={<>
             <button onClick={onClose} className="h-10 px-4 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 hover:bg-slate-50">Batal</button>
             <button onClick={simpan} disabled={busy} data-testid="mkt-content-create-simpan"
                     className="h-10 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium disabled:opacity-50">
               {busy ? "Menyimpan..." : "Simpan"}
             </button>
           </>}>
      {form && (
        <>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Brand" required>
              <InputCabang value={form.brand} onChange={(v) => setForm((f) => ({ ...f, brand: v }))} opsi={brandOpsi} testid="mkt-content-create-f-brand" />
            </Field>
            <Field label="Tanggal Rencana Tayang" required>
              <DatePicker value={form.date_plan} onChange={(v) => setForm((f) => ({ ...f, date_plan: v }))} data-testid="mkt-content-create-f-tanggal" />
            </Field>
          </div>
          <Field label="Brand / Toko" hint="Boleh pilih lebih dari satu toko — daftar khusus Marketing, bukan cabang perusahaan">
            <PilihToko value={form.toko} onChange={(v) => setForm((f) => ({ ...f, toko: v }))}
                       opsi={tokoOpsi} testid="mkt-content-create-f-toko" />
          </Field>
          {bolehCampaign && (
            <Field label="Campaign (opsional)">
              <select value={form.campaign_id} onChange={(e) => setForm((f) => ({ ...f, campaign_id: e.target.value }))} data-testid="mkt-content-create-f-campaign" className={selectCls}>
                <option value="">— tidak terkait campaign —</option>
                {campaignList.map((c) => <option key={c.id} value={c.id}>{c.code} — {c.nama}</option>)}
              </select>
            </Field>
          )}
          <div className="grid grid-cols-2 gap-3">
            <Field label="PIC (opsional)">
              <select value={form.pic_user_id} onChange={(e) => setForm((f) => ({ ...f, pic_user_id: e.target.value }))}
                      data-testid="mkt-content-create-f-pic" className={selectCls}>
                <option value="">— belum ditentukan —</option>
                {picOptions.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            </Field>
            <Field label="Deadline Posting (opsional)" hint="Batas akhir tayang — beda dari tanggal rencana di atas, boleh digeser">
              <DatePicker value={form.deadline_post} onChange={(v) => setForm((f) => ({ ...f, deadline_post: v }))} data-testid="mkt-content-create-f-deadline" />
            </Field>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <Field label="Platform" required>
              <select value={form.platform} onChange={(e) => setForm((f) => ({ ...f, platform: e.target.value }))} data-testid="mkt-content-create-f-platform" className={selectCls}>
                {CONTENT_PLATFORMS.map((p) => <option key={p} value={p}>{PLATFORM_LABEL[p]}</option>)}
              </select>
            </Field>
            <Field label="Format" required>
              <select value={form.format} onChange={(e) => setForm((f) => ({ ...f, format: e.target.value }))} data-testid="mkt-content-create-f-format" className={selectCls}>
                {CONTENT_FORMATS.map((f) => <option key={f} value={f}>{CONTENT_FORMAT_LABEL[f]}</option>)}
              </select>
            </Field>
            <Field label="Pillar" required>
              <select value={form.pillar} onChange={(e) => setForm((f) => ({ ...f, pillar: e.target.value }))} data-testid="mkt-content-create-f-pillar" className={selectCls}>
                {CONTENT_PILLARS.map((p) => <option key={p} value={p}>{CONTENT_PILLAR_LABEL[p]}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Judul" required>
            <input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} data-testid="mkt-content-create-f-title" className={inputCls} />
          </Field>
          <Field label="Hook">
            <input value={form.hook} onChange={(e) => setForm((f) => ({ ...f, hook: e.target.value }))} data-testid="mkt-content-create-f-hook" className={inputCls} placeholder="Kalimat pembuka penarik perhatian" />
          </Field>
          <Field label="Caption">
            <textarea value={form.caption} rows={2} onChange={(e) => setForm((f) => ({ ...f, caption: e.target.value }))} data-testid="mkt-content-create-f-caption" className={textareaCls} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="CTA">
              <input value={form.cta} onChange={(e) => setForm((f) => ({ ...f, cta: e.target.value }))} data-testid="mkt-content-create-f-cta" className={inputCls} />
            </Field>
            <Field label="Link Asset">
              <input value={form.asset_url} onChange={(e) => setForm((f) => ({ ...f, asset_url: e.target.value }))} data-testid="mkt-content-create-f-asset" className={inputCls} placeholder="Link ke file mentah/desain" />
            </Field>
          </div>
          <Field label="Referensi Konten" hint="Beberapa link sekaligus — riset, contoh konten, brief desain">
            <EditorReferensi value={form.referensi} onChange={(v) => setForm((f) => ({ ...f, referensi: v }))}
                             testid="mkt-content-create-f-referensi" />
          </Field>
          <Field label="Catatan">
            <textarea value={form.catatan} rows={2} onChange={(e) => setForm((f) => ({ ...f, catatan: e.target.value }))} data-testid="mkt-content-create-f-catatan" className={textareaCls} />
          </Field>
        </>
      )}
    </Modal>
  );
}

// ============================================================================
// Modal Detail — edit field, transisi status, approve/reject, tandai tayang,
// pindah tanggal, hapus, dan performa organik.
// ============================================================================
function ContentDetailModal({ id, onClose, onChanged, bolehEdit, bolehHapus, bolehTambah, bolehCampaign, campaignList, campaignMap, brandOpsi, tokoOpsi, picOptions }) {
  const { data: c, loading, error, refetch } = useDataQuery(
    ["marketing", "content-detail", id],
    () => api.get(`/marketing/content/${id}`).then((r) => r.data),
    { enabled: !!id },
  );
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState(null);
  const [tanggalBaru, setTanggalBaru] = useState("");
  const [showReject, setShowReject] = useState(false);
  const [alasanTolak, setAlasanTolak] = useState("");
  const [showPosted, setShowPosted] = useState(false);
  const [postedUrl, setPostedUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const { dialog: pinDialog, setDialog: setPinDialog, hapusDenganPin, submitDialog } = usePinHapus();

  useEffect(() => {
    if (c && !editMode) setTanggalBaru(c.date_plan);
    if (!id) { setEditMode(false); setShowReject(false); setShowPosted(false); }
  }, [c, editMode, id]);

  if (!id) return null;

  const mulaiEdit = () => {
    setForm({
      brand: c.brand, campaign_id: c.campaign_id || "", platform: c.platform, format: c.format, pillar: c.pillar,
      title: c.title, hook: c.hook || "", caption: c.caption || "", cta: c.cta || "", asset_url: c.asset_url || "", catatan: c.catatan || "",
      pic_user_id: c.pic_user_id || "", deadline_post: c.deadline_post || "",
      // Konten LAMA tidak punya kedua field ini — backend sudah menormalkannya
      // jadi `[]` di respons, `|| []` di sini cuma sabuk pengaman kedua.
      toko: c.toko || [], referensi: (c.referensi || []).map((r) => ({ judul: r.judul || "", url: r.url || "" })),
    });
    setEditMode(true);
  };

  const simpanEdit = async () => {
    if (!form.brand.trim()) { toast.error("Brand wajib diisi"); return; }
    if (!form.title.trim()) { toast.error("Judul wajib diisi"); return; }
    const galatRef = periksaReferensi(form.referensi);
    if (galatRef) { toast.error(galatRef); return; }
    setBusy(true);
    try {
      // pic_user_id & deadline_post SENGAJA dikirim apa adanya (bukan `|| null`
      // seperti campaign_id di atas) — backend membedakan "" (hapus nilai) dari
      // field yang sama sekali tidak dikirim (biarkan), lihat docstring
      // `ContentUpdateReq` di content.py. Form ini selalu diisi (bukan `null`)
      // dari `mulaiEdit`, jadi mengirim `form` apa adanya sudah benar.
      //
      // `toko`/`referensi` juga dikirim apa adanya sebagai LARIK: larik kosong
      // `[]` adalah perintah "kosongkan" yang lolos saringan backend, sementara
      // `null` justru diabaikan. Pola `nilai || null` (lihat `campaign_id` tepat
      // di baris bawah) SENGAJA tidak diulang untuk keduanya.
      await api.patch(`/marketing/content/${id}`, {
        ...form, campaign_id: form.campaign_id || null,
        toko: form.toko, referensi: bersihkanReferensi(form.referensi),
      });
      toast.success("Konten diperbarui");
      setEditMode(false); refetch(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan konten"); }
    finally { setBusy(false); }
  };

  const pindah = async () => {
    if (!tanggalBaru || tanggalBaru === c.date_plan) return;
    setBusy(true);
    try {
      const r = await api.patch(`/marketing/content/${id}/tanggal`, { date_plan: tanggalBaru });
      if (r.data.deadline_terlewat) toast.warning(r.data.message);
      else toast.success(r.data.message || "Tanggal rencana tayang dipindah");
      refetch(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal memindah tanggal"); }
    finally { setBusy(false); }
  };

  const ubahStatus = async (target) => {
    setBusy(true);
    try {
      await api.patch(`/marketing/content/${id}/status`, { status: target });
      toast.success(`Status dipindah ke "${CONTENT_STATUS_META[target]?.label || target}"`);
      refetch(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal mengubah status"); }
    finally { setBusy(false); }
  };

  const setujui = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/marketing/content/${id}/approve`);
      toast.success(r.data.message || "Konten disetujui");
      refetch(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyetujui konten"); }
    finally { setBusy(false); }
  };

  const tolak = async () => {
    if (!alasanTolak.trim()) { toast.error("Alasan penolakan wajib diisi"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/marketing/content/${id}/reject`, { alasan: alasanTolak });
      toast.success(r.data.message || "Konten dikirim balik untuk revisi");
      setShowReject(false); setAlasanTolak(""); refetch(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menolak konten"); }
    finally { setBusy(false); }
  };

  const tandaiTayang = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/marketing/content/${id}/posted`, { posted_url: postedUrl });
      toast.success(r.data.message || "Konten ditandai tayang");
      setShowPosted(false); refetch(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menandai konten tayang"); }
    finally { setBusy(false); }
  };

  const hapus = async () => {
    if (!window.confirm(`Hapus konten "${c.title}"?`)) return;
    const aksi = (pin) => api.delete(`/marketing/content/${id}`, pin ? { params: { pin } } : undefined);
    const langsung = await hapusDenganPin(aksi, { modul: "marketing_content", targetId: id, ringkasan: `Hapus konten ${c.title}` });
    if (langsung) { toast.success("Konten dihapus"); onClose(); onChanged?.(); }
  };

  const targets = c ? (MANUAL_TRANSITIONS[c.status] || []) : [];
  // Banner revisi HANYA selagi status masih "draft" — begitu diajukan ulang
  // dan berpindah status, ini otomatis hilang (lihat docstring komponen induk).
  const tampilkanRevisi = c && c.status === "draft" && c.rejection_reason;

  return (
    <Modal open={!!id} onClose={onClose} title={c ? `${c.code} — ${c.title}` : "Detail Konten"} maxWidth="max-w-2xl" testid="mkt-content-detail-modal">
      <LoadingState loading={loading} error={error} onRetry={refetch} varian="kartu" data-testid="mkt-content-detail-loading">
        {c && (
          <div className="space-y-4" data-testid="mkt-content-detail-body">
            <div className="flex items-center justify-between flex-wrap gap-2">
              <StatusBadge meta={CONTENT_STATUS_META} value={c.status} />
              {bolehEdit && !editMode && c.status !== "posted" && (
                <button onClick={mulaiEdit} data-testid="mkt-content-detail-edit"
                        className="inline-flex items-center gap-1 text-xs font-medium text-slate-600 hover:text-emerald-700">
                  <Pencil className="w-3.5 h-3.5" /> Edit
                </button>
              )}
            </div>

            {tampilkanRevisi && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2.5" data-testid="mkt-content-detail-revisi">
                <p className="text-xs font-semibold text-amber-800 inline-flex items-center gap-1.5"><Info className="w-3.5 h-3.5" /> Perlu revisi</p>
                <p className="text-xs text-amber-700 mt-1">{c.rejection_reason}</p>
                <p className="text-[11px] text-amber-600 mt-1">Perbaiki lalu ajukan kembali untuk review — ini BUKAN penolakan final.</p>
              </div>
            )}

            {/* Konten lama tanpa deadline_post selalu deadline_terlewat=false
                (lihat _hitung_deadline_terlewat di backend) — banner ini TIDAK
                PERNAH tampil untuk konten yang belum pernah diberi deadline. */}
            {c.deadline_terlewat && (
              <div className="bg-rose-50 border border-rose-200 rounded-lg px-3 py-2.5" data-testid="mkt-content-detail-deadline-terlewat">
                <p className="text-xs font-semibold text-rose-800 inline-flex items-center gap-1.5"><AlertTriangle className="w-3.5 h-3.5" /> Deadline posting terlewat</p>
                <p className="text-xs text-rose-700 mt-1">
                  Tanggal rencana tayang ({formatTanggal(c.date_plan)}) sudah melewati deadline posting ({formatTanggal(c.deadline_post)}).
                </p>
              </div>
            )}

            {editMode ? (
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <Field label="Brand" required>
                    <InputCabang value={form.brand} onChange={(v) => setForm((f) => ({ ...f, brand: v }))} opsi={brandOpsi} testid="mkt-content-detail-f-brand" />
                  </Field>
                  {bolehCampaign && (
                    <Field label="Campaign">
                      <select value={form.campaign_id} onChange={(e) => setForm((f) => ({ ...f, campaign_id: e.target.value }))} data-testid="mkt-content-detail-f-campaign" className={selectCls}>
                        <option value="">— tidak terkait —</option>
                        {campaignList.map((cp) => <option key={cp.id} value={cp.id}>{cp.code} — {cp.nama}</option>)}
                      </select>
                    </Field>
                  )}
                </div>
                <Field label="Brand / Toko" hint="Boleh lebih dari satu — kosongkan semua kalau konten ini tidak khusus toko mana pun">
                  <PilihToko value={form.toko} onChange={(v) => setForm((f) => ({ ...f, toko: v }))}
                             opsi={tokoOpsi} testid="mkt-content-detail-f-toko" />
                </Field>
                <Field label="Referensi Konten" hint="URL wajib http:// atau https://">
                  <EditorReferensi value={form.referensi} onChange={(v) => setForm((f) => ({ ...f, referensi: v }))}
                                   testid="mkt-content-detail-f-referensi" />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="PIC">
                    <select value={form.pic_user_id} onChange={(e) => setForm((f) => ({ ...f, pic_user_id: e.target.value }))}
                            data-testid="mkt-content-detail-f-pic" className={selectCls}>
                      <option value="">— belum ditentukan —</option>
                      {picOptions.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
                    </select>
                  </Field>
                  <Field label="Deadline Posting" hint="Kosongkan untuk menghapus deadline">
                    <DatePicker value={form.deadline_post} onChange={(v) => setForm((f) => ({ ...f, deadline_post: v }))} data-testid="mkt-content-detail-f-deadline" />
                  </Field>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <Field label="Platform">
                    <select value={form.platform} onChange={(e) => setForm((f) => ({ ...f, platform: e.target.value }))} data-testid="mkt-content-detail-f-platform" className={selectCls}>
                      {CONTENT_PLATFORMS.map((p) => <option key={p} value={p}>{PLATFORM_LABEL[p]}</option>)}
                    </select>
                  </Field>
                  <Field label="Format">
                    <select value={form.format} onChange={(e) => setForm((f) => ({ ...f, format: e.target.value }))} data-testid="mkt-content-detail-f-format" className={selectCls}>
                      {CONTENT_FORMATS.map((f) => <option key={f} value={f}>{CONTENT_FORMAT_LABEL[f]}</option>)}
                    </select>
                  </Field>
                  <Field label="Pillar">
                    <select value={form.pillar} onChange={(e) => setForm((f) => ({ ...f, pillar: e.target.value }))} data-testid="mkt-content-detail-f-pillar" className={selectCls}>
                      {CONTENT_PILLARS.map((p) => <option key={p} value={p}>{CONTENT_PILLAR_LABEL[p]}</option>)}
                    </select>
                  </Field>
                </div>
                <Field label="Judul" required>
                  <input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} data-testid="mkt-content-detail-f-title" className={inputCls} />
                </Field>
                <Field label="Hook">
                  <input value={form.hook} onChange={(e) => setForm((f) => ({ ...f, hook: e.target.value }))} data-testid="mkt-content-detail-f-hook" className={inputCls} />
                </Field>
                <Field label="Caption">
                  <textarea value={form.caption} rows={2} onChange={(e) => setForm((f) => ({ ...f, caption: e.target.value }))} data-testid="mkt-content-detail-f-caption" className={textareaCls} />
                </Field>
                <div className="grid grid-cols-2 gap-3">
                  <Field label="CTA">
                    <input value={form.cta} onChange={(e) => setForm((f) => ({ ...f, cta: e.target.value }))} data-testid="mkt-content-detail-f-cta" className={inputCls} />
                  </Field>
                  <Field label="Link Asset">
                    <input value={form.asset_url} onChange={(e) => setForm((f) => ({ ...f, asset_url: e.target.value }))} data-testid="mkt-content-detail-f-asset" className={inputCls} />
                  </Field>
                </div>
                <Field label="Catatan">
                  <textarea value={form.catatan} rows={2} onChange={(e) => setForm((f) => ({ ...f, catatan: e.target.value }))} data-testid="mkt-content-detail-f-catatan" className={textareaCls} />
                </Field>
                <div className="flex justify-end gap-2">
                  <button onClick={() => setEditMode(false)} className="h-9 px-3 rounded-lg border border-slate-200 text-xs font-medium text-slate-600">Batal</button>
                  <button onClick={simpanEdit} disabled={busy} data-testid="mkt-content-detail-simpan"
                          className="h-9 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium disabled:opacity-50">
                    {busy ? "Menyimpan..." : "Simpan Perubahan"}
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-2 text-sm">
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-slate-600">
                  <span>Brand: <span className="font-medium text-slate-800">{c.brand}</span></span>
                  {c.campaign_id && <span>Campaign: <span className="font-medium text-slate-800">{namaCampaign(campaignMap, c.campaign_id)}</span></span>}
                  <span>Platform: <span className="font-medium text-slate-800">{PLATFORM_LABEL[c.platform] || c.platform}</span></span>
                  <span>Format: <span className="font-medium text-slate-800">{CONTENT_FORMAT_LABEL[c.format] || c.format}</span></span>
                  <span>Pillar: <span className="font-medium text-slate-800">{CONTENT_PILLAR_LABEL[c.pillar] || c.pillar}</span></span>
                  {c.pic_nama && <span>PIC: <span className="font-medium text-slate-800">{c.pic_nama}</span></span>}
                  {c.deadline_post && (
                    <span>Deadline Posting: <span className={`font-medium ${c.deadline_terlewat ? "text-rose-600" : "text-slate-800"}`}>{formatTanggal(c.deadline_post)}</span></span>
                  )}
                </div>
                {(c.toko || []).length > 0 && (
                  <div className="flex flex-wrap items-center gap-1.5" data-testid="mkt-content-detail-toko">
                    <span className="text-xs text-slate-600">Brand / Toko:</span>
                    {(c.toko || []).map((t) => (
                      <span key={t} data-testid={`mkt-content-detail-toko-${t}`}
                            className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 text-[11px] font-medium">{t}</span>
                    ))}
                  </div>
                )}
                {c.hook && <p className="text-xs text-slate-500"><span className="font-medium text-slate-700">Hook:</span> {c.hook}</p>}
                {c.caption && <p className="text-xs text-slate-500 whitespace-pre-wrap"><span className="font-medium text-slate-700">Caption:</span> {c.caption}</p>}
                {c.cta && <p className="text-xs text-slate-500"><span className="font-medium text-slate-700">CTA:</span> {c.cta}</p>}
                {c.asset_url && <p className="text-xs"><a href={c.asset_url} target="_blank" rel="noreferrer" className="text-emerald-700 hover:underline">Link asset</a></p>}
                <DaftarReferensi items={c.referensi} testid="mkt-content-detail-referensi" />
                {c.catatan && <p className="text-xs text-slate-500"><span className="font-medium text-slate-700">Catatan:</span> {c.catatan}</p>}
                {c.status === "posted" && (
                  <p className="text-xs text-slate-500">
                    Tayang {formatTanggal(c.posted_at, { denganJam: true })} oleh {c.posted_by}
                    {c.posted_url && <> — <a href={c.posted_url} target="_blank" rel="noreferrer" className="text-emerald-700 hover:underline">lihat postingan</a></>}
                  </p>
                )}
              </div>
            )}

            {/* Pindah tanggal — hanya kalau belum posted */}
            {bolehEdit && c.status !== "posted" && !editMode && (
              <div className="flex items-end gap-2 border-t border-slate-100 pt-3">
                <div className="flex-1">
                  <Field label="Tanggal Rencana Tayang">
                    <DatePicker value={tanggalBaru} onChange={setTanggalBaru} data-testid="mkt-content-detail-f-tanggal" />
                  </Field>
                </div>
                <button onClick={pindah} disabled={busy || tanggalBaru === c.date_plan} data-testid="mkt-content-detail-pindah-tanggal"
                        className="h-10 px-3 rounded-lg border border-slate-200 hover:bg-slate-50 text-xs font-medium text-slate-700 disabled:opacity-40">
                  Pindah Tanggal
                </button>
              </div>
            )}

            {/* Aksi status */}
            {bolehEdit && !editMode && (targets.length > 0 || c.status === "review" || c.status === "scheduled") && (
              <div className="border-t border-slate-100 pt-3">
                <p className="text-[11px] font-semibold text-slate-500 mb-2">Aksi Status</p>
                <div className="flex items-center gap-2 flex-wrap">
                  {targets.map((t) => (
                    <button key={t} onClick={() => ubahStatus(t)} disabled={busy} data-testid={`mkt-content-status-${t}`}
                            className="h-8 px-3 rounded-lg border border-slate-200 hover:bg-slate-50 text-xs font-medium text-slate-700 inline-flex items-center gap-1.5">
                      {t === "review" ? <Send className="w-3.5 h-3.5" /> : <RotateCcw className="w-3.5 h-3.5" />} {TRANSITION_LABEL[`${c.status}->${t}`]}
                    </button>
                  ))}
                  {c.status === "review" && (
                    <>
                      <button onClick={setujui} disabled={busy} data-testid="mkt-content-setujui"
                              className="h-8 px-3 rounded-lg bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-xs font-medium inline-flex items-center gap-1.5">
                        <CheckCircle2 className="w-3.5 h-3.5" /> Setujui
                      </button>
                      <button onClick={() => setShowReject((s) => !s)} data-testid="mkt-content-tolak"
                              className="h-8 px-3 rounded-lg bg-rose-50 hover:bg-rose-100 text-rose-700 text-xs font-medium inline-flex items-center gap-1.5">
                        <XCircle className="w-3.5 h-3.5" /> Tolak (Minta Revisi)
                      </button>
                    </>
                  )}
                  {c.status === "scheduled" && (
                    <button onClick={() => setShowPosted((s) => !s)} data-testid="mkt-content-tandai-tayang"
                            className="h-8 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium inline-flex items-center gap-1.5">
                      <Rocket className="w-3.5 h-3.5" /> Tandai Tayang
                    </button>
                  )}
                </div>
                {showReject && (
                  <div className="mt-2 flex items-end gap-2">
                    <div className="flex-1">
                      <Field label="Alasan minta revisi" required>
                        <input value={alasanTolak} onChange={(e) => setAlasanTolak(e.target.value)} data-testid="mkt-content-tolak-alasan" className={inputCls} placeholder="cth. Caption kurang jelas, foto blur" />
                      </Field>
                    </div>
                    <button onClick={tolak} disabled={busy} data-testid="mkt-content-tolak-kirim"
                            className="h-10 px-3 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-xs font-medium disabled:opacity-50">Kirim</button>
                  </div>
                )}
                {showPosted && (
                  <div className="mt-2 flex items-end gap-2">
                    <div className="flex-1">
                      <Field label="Link postingan (opsional)">
                        <input value={postedUrl} onChange={(e) => setPostedUrl(e.target.value)} data-testid="mkt-content-posted-url" className={inputCls} placeholder="https://..." />
                      </Field>
                    </div>
                    <button onClick={tandaiTayang} disabled={busy} data-testid="mkt-content-posted-kirim"
                            className="h-10 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium disabled:opacity-50">Tandai Tayang</button>
                  </div>
                )}
              </div>
            )}

            {/* Performa organik */}
            {!editMode && (
              <PerformanceSection contentId={id} postedAt={c.posted_at} performance={c.performance || []}
                                  bolehTambah={bolehTambah} bolehEdit={bolehEdit} bolehHapus={bolehHapus}
                                  onChanged={() => { refetch(); onChanged?.(); }} />
            )}

            {bolehHapus && c.status !== "posted" && !editMode && (
              <div className="border-t border-slate-100 pt-3 flex justify-end">
                <button onClick={hapus} data-testid="mkt-content-detail-hapus"
                        className="h-8 px-3 rounded-lg border border-slate-200 hover:bg-rose-50 text-rose-600 text-xs font-medium inline-flex items-center gap-1.5">
                  <Trash2 className="w-3.5 h-3.5" /> Hapus Konten
                </button>
              </div>
            )}
          </div>
        )}
      </LoadingState>
      <PinHapusDialog dialog={pinDialog} setDialog={setPinDialog} onSubmit={() => submitDialog(() => onChanged?.())} />
    </Modal>
  );
}

// ============================================================================
// Snapshot performa organik (brief butir 4) — H+1/H+7 quick-fill dari posted_at.
// ============================================================================
const PERF_FIELDS = [
  { key: "views", label: "Views" }, { key: "likes", label: "Likes" },
  { key: "comments", label: "Komentar" }, { key: "shares", label: "Shares" },
  { key: "saves", label: "Saves" }, { key: "reach", label: "Reach" },
  { key: "profile_visits", label: "Profile Visits" }, { key: "follows", label: "Follows Baru" },
];

function emptyPerfForm(snapshot_date) {
  const f = { snapshot_date };
  PERF_FIELDS.forEach((p) => { f[p.key] = 0; });
  return f;
}

function tambahHari(isoTanggal, n) {
  const d = dateDariIso((isoTanggal || "").slice(0, 10)) || new Date();
  const hasil = new Date(d);
  hasil.setDate(hasil.getDate() + n);
  return isoDariDate(hasil);
}

function PerformanceSection({ contentId, postedAt, performance, bolehTambah, bolehEdit, bolehHapus, onChanged }) {
  const [form, setForm] = useState(null); // {id?, snapshot_date, views, ...}
  const [busy, setBusy] = useState(false);
  const { dialog: pinDialog, setDialog: setPinDialog, hapusDenganPin, submitDialog } = usePinHapus();

  const simpan = async () => {
    if (!form.snapshot_date) { toast.error("Tanggal snapshot wajib diisi"); return; }
    setBusy(true);
    // Cast eksplisit ke Number — input number HTML mengirim string mentah
    // lewat onChange, backend Pydantic mengharapkan int murni.
    const metrik = Object.fromEntries(PERF_FIELDS.map((p) => [p.key, Number(form[p.key] || 0)]));
    try {
      if (form.id) {
        await api.patch(`/marketing/content/performance/${form.id}`, metrik);
      } else {
        await api.post("/marketing/content/performance", { content_id: contentId, snapshot_date: form.snapshot_date, ...metrik });
      }
      toast.success("Snapshot performa tersimpan");
      setForm(null); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan snapshot performa"); }
    finally { setBusy(false); }
  };

  const hapus = async (p) => {
    if (!window.confirm(`Hapus snapshot ${p.snapshot_date}?`)) return;
    const aksi = (pin) => api.delete(`/marketing/content/performance/${p.id}`, pin ? { params: { pin } } : undefined);
    const langsung = await hapusDenganPin(aksi, { modul: "marketing_content", targetId: p.id, ringkasan: `Hapus snapshot performa ${p.snapshot_date}` });
    if (langsung) { toast.success("Snapshot dihapus"); onChanged?.(); }
  };

  return (
    <div className="border-t border-slate-100 pt-3" data-testid="mkt-content-performance">
      <div className="flex items-center justify-between mb-2">
        <p className="text-[11px] font-semibold text-slate-500">Performa Organik</p>
        {bolehTambah && !form && (
          <div className="flex items-center gap-1.5">
            {postedAt && (
              <>
                <button onClick={() => setForm(emptyPerfForm(tambahHari(postedAt, 1)))} data-testid="mkt-content-perf-hplus1"
                        className="h-7 px-2 rounded border border-slate-200 hover:bg-slate-50 text-[11px] text-slate-600 font-medium">H+1</button>
                <button onClick={() => setForm(emptyPerfForm(tambahHari(postedAt, 7)))} data-testid="mkt-content-perf-hplus7"
                        className="h-7 px-2 rounded border border-slate-200 hover:bg-slate-50 text-[11px] text-slate-600 font-medium">H+7</button>
              </>
            )}
            <button onClick={() => setForm(emptyPerfForm(hariIniISO()))} data-testid="mkt-content-perf-tambah"
                    className="h-7 px-2 rounded bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-[11px] font-medium inline-flex items-center gap-1">
              <Plus className="w-3 h-3" /> Snapshot
            </button>
          </div>
        )}
      </div>

      {performance.length === 0 && !form && <p className="text-xs text-slate-400">Belum ada snapshot performa.</p>}

      {performance.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-[11px]">
            <thead className="text-slate-400">
              <tr className="text-left">
                <th className="py-1 pr-2">Tanggal</th>
                {PERF_FIELDS.map((p) => <th key={p.key} className="py-1 px-2 text-right">{p.label}</th>)}
                {(bolehEdit || bolehHapus) && <th className="py-1 pl-2 text-right">Aksi</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {performance.map((p) => (
                <tr key={p.id} data-testid={`mkt-content-perf-row-${p.id}`}>
                  <td className="py-1.5 pr-2 text-slate-600 whitespace-nowrap">{formatTanggal(p.snapshot_date)}{p.h_plus != null ? ` (H+${p.h_plus})` : ""}</td>
                  {PERF_FIELDS.map((f) => <td key={f.key} className="py-1.5 px-2 text-right font-mono">{fmtNum(p[f.key])}</td>)}
                  {(bolehEdit || bolehHapus) && (
                    <td className="py-1.5 pl-2 text-right whitespace-nowrap">
                      {bolehEdit && (
                        <button onClick={() => setForm({ id: p.id, snapshot_date: p.snapshot_date, ...Object.fromEntries(PERF_FIELDS.map((f) => [f.key, p[f.key] || 0])) })}
                                data-testid={`mkt-content-perf-edit-${p.id}`} className="text-slate-500 hover:text-emerald-700 mr-1.5">
                          <Pencil className="w-3 h-3 inline" />
                        </button>
                      )}
                      {bolehHapus && (
                        <button onClick={() => hapus(p)} data-testid={`mkt-content-perf-hapus-${p.id}`} className="text-slate-500 hover:text-rose-600">
                          <Trash2 className="w-3 h-3 inline" />
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {form && (
        <div className="mt-2 border border-slate-200 rounded-lg p-3 bg-slate-50/60 space-y-2" data-testid="mkt-content-perf-form">
          <div className="flex items-center justify-between">
            <Field label="Tanggal Snapshot">
              <DatePicker value={form.snapshot_date} onChange={(v) => setForm((f) => ({ ...f, snapshot_date: v }))} data-testid="mkt-content-perf-f-tanggal" className="w-40" />
            </Field>
            <button onClick={() => setForm(null)} className="text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
          </div>
          <div className="grid grid-cols-4 gap-2">
            {PERF_FIELDS.map((f) => (
              <div key={f.key}>
                <label className="text-[10px] text-slate-500">{f.label}</label>
                <input type="number" min="0" value={form[f.key]} onChange={(e) => setForm((s) => ({ ...s, [f.key]: e.target.value }))}
                       data-testid={`mkt-content-perf-f-${f.key}`} className={`${inputCls} h-8 text-sm`} />
              </div>
            ))}
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => setForm(null)} className="h-8 px-3 rounded-lg border border-slate-200 text-xs">Batal</button>
            <button onClick={simpan} disabled={busy} data-testid="mkt-content-perf-simpan"
                    className="h-8 px-3 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium disabled:opacity-50">
              {busy ? "Menyimpan..." : "Simpan Snapshot"}
            </button>
          </div>
        </div>
      )}
      <PinHapusDialog dialog={pinDialog} setDialog={setPinDialog} onSubmit={() => submitDialog(() => onChanged?.())} />
    </div>
  );
}
